import { useCallback, useEffect } from 'react';

import { Button } from '@components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import formatterDecimal from '@formatters/decimal';
import { CircleHelp } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { NumberFormatBase } from 'react-number-format';
import { z } from 'zod';

import { useHidratacaoPediatria } from '../context';
import type { formSchema } from '../formSchema';

export function IsotonicSolution() {
  const form = useFormContext<z.infer<typeof formSchema>>();
  const {
    caloricWeight,
    dailyVolume,
    dailyVolume50Percent,
    KClCalculated,
    NaClIsotonicSolution,
    setKCl,
    setMEq,
  } = useHidratacaoPediatria();

  const handleChangeMEq = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setMEq(valueParsed);
    },
    [setMEq],
  );

  const handleChangeKCl = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setKCl(valueParsed);
    },
    [setKCl],
  );

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'mEq' && value.mEq) {
        handleChangeMEq(value.mEq);
      }
      if (name === 'KCl' && value.KCl) {
        handleChangeKCl(value.KCl);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <div>
      <div className="mb-4 flex flex-row justify-center gap-6">
        <FormField
          control={form.control}
          name="mEq"
          render={({ field: { ref, ...rest } }) => (
            <FormItem className="flex flex-col items-center">
              <FormLabel>
                Concentração de
                <br />
                NaCl
              </FormLabel>
              <FormControl>
                <NumberFormatBase
                  className="[&_input]:w-26 [&_input]:text-center"
                  customInput={Input}
                  format={(value) => formatterDecimal(value, 1)}
                  getCaretBoundary={(formattedValue) => {
                    const boundaryAry = Array.from({
                      length: formattedValue.length + 1,
                    }).map(() => true);
                    boundaryAry[0] = false;
                    boundaryAry[1] = false;
                    boundaryAry[2] = false;
                    return boundaryAry;
                  }}
                  getInputRef={ref}
                  {...rest}
                />
              </FormControl>
              <FormDescription className="font-light">mEq/L</FormDescription>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="KCl"
          render={({ field: { ref, ...rest } }) => (
            <FormItem className="flex flex-col items-center">
              <FormLabel>
                Necessidade
                <br />
                de KCl/dia
              </FormLabel>
              <FormControl>
                <NumberFormatBase
                  className="[&_input]:w-26 [&_input]:text-center"
                  customInput={Input}
                  format={(value) => formatterDecimal(value, 1)}
                  getCaretBoundary={(formattedValue) => {
                    const boundaryAry = Array.from({
                      length: formattedValue.length + 1,
                    }).map(() => true);
                    boundaryAry[0] = false;
                    boundaryAry[1] = false;
                    boundaryAry[2] = false;
                    return boundaryAry;
                  }}
                  getInputRef={ref}
                  {...rest}
                />
              </FormControl>
              <FormDescription className="font-light">
                mEq/kg/dia
              </FormDescription>
            </FormItem>
          )}
        />
      </div>

      <div className="flex flex-row items-center justify-center">
        <Label className="text-lg">Peso calórico</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button className="cursor-pointer" size="icon" variant="ghost">
              <CircleHelp className="text-foreground" strokeWidth={2} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-lg space-y-4">
            <h1 className="text-secondary text-center font-medium">
              Informação
            </h1>
            <p className="text-foreground flex-1 text-sm">
              A fórmula de Holliday e Segar estima uma relação entre o volume de
              líquidos necessário e o gasto energético basal da criança na
              proporção de 100mL/Kcal/dia.
              <br />
              <br />
              Peso calórico (Kcal) = Volume diário (mL) / 100
            </p>
          </PopoverContent>
        </Popover>
      </div>

      <div className="mb-4 flex flex-row items-center justify-center gap-2">
        <p>
          <span className="text-tertiary text-lg font-bold">
            {caloricWeight.toFixed(1)}
          </span>{' '}
        </p>
        <Label className="text-foreground text-sm font-light">kcal</Label>
      </div>

      <div className="mx-auto w-fit">
        <div className="flex flex-row items-center justify-start">
          <Label className="text-lg">1º - Volume diário (Holliday)</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button className="cursor-pointer" size="icon" variant="ghost">
                <CircleHelp className="text-foreground" strokeWidth={2} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full max-w-lg space-y-4">
              <h1 className="text-secondary text-center font-medium">
                Recomendação
              </h1>
              <p className="text-foreground flex-1 text-sm">
                Até 10Kg: 100mL/Kcal
                <br />
                <br />
                De 10 a 20Kg: 1.000mL + 50mL para cada Kcal acima de 10Kcal
                <br />
                <br />
                Maior que 20Kg: 1.500mL + 20mL para cada Kcal acima de 20Kcal
                <br />
                <br />
                Máximo: 2.400 mL/dia
              </p>
            </PopoverContent>
          </Popover>
        </div>
        <div className="ml-8 flex flex-row items-center justify-start gap-2">
          <p>
            <span className="text-tertiary text-lg font-bold">
              {dailyVolume}
            </span>{' '}
          </p>
          <Label className="text-foreground text-sm font-light">
            mL de SG 5%, em 24 horas, ou
          </Label>
        </div>
        <div className="mb-4 ml-8 flex flex-row items-center justify-start gap-2">
          <p>
            <span className="text-tertiary text-lg font-bold">
              {dailyVolume50Percent}
            </span>{' '}
          </p>
          <Label className="text-foreground text-sm font-light">
            mL de SG 5%, de 12/12 horas
          </Label>
        </div>

        <div className="flex flex-row items-center justify-start">
          <Label className="text-lg">
            2º - NaCl 20%{' '}
            <Label className="text-foreground text-sm font-light">
              (1mL = 3,4 mEq)
            </Label>
          </Label>
        </div>
        <div className="mb-4 ml-8 flex flex-row items-center justify-start gap-2">
          <p>
            <span className="text-tertiary text-lg font-bold">
              {NaClIsotonicSolution.toFixed(1)}
            </span>{' '}
          </p>
          <Label className="text-foreground text-sm font-light">
            mL de NaCl 20% de 12/12 horas
          </Label>
        </div>

        <div className="flex flex-row items-center justify-start">
          <Label className="text-lg">
            3º - KCl 19,1%{' '}
            <Label className="text-foreground text-sm font-light">
              (1 mL = 2,5 mEq)
            </Label>
          </Label>
        </div>
        <div className="mb-4 ml-8 flex flex-row items-center justify-start gap-2">
          <p>
            <span className="text-tertiary text-lg font-bold">
              {KClCalculated.toFixed(1)}
            </span>{' '}
          </p>
          <Label className="text-foreground text-sm font-light">
            mL de KCl 19,1% de 12/12 horas
          </Label>
        </div>

        <div className="flex flex-row items-center justify-start">
          <Label className="text-lg">4º - Prescrição</Label>
        </div>
        <div className="ml-8 flex flex-row items-center justify-start gap-2">
          <p>
            <span className="text-tertiary text-lg font-bold">
              {dailyVolume50Percent}
            </span>{' '}
          </p>
          <Label className="text-foreground text-sm font-light">
            mL de SG 5% +
          </Label>
        </div>
        <div className="ml-8 flex flex-row items-center justify-start gap-2">
          <p>
            <span className="text-tertiary text-lg font-bold">
              {NaClIsotonicSolution.toFixed(1)}
            </span>{' '}
          </p>
          <Label className="text-foreground text-sm font-light">
            mL de NaCl 20% +
          </Label>
        </div>
        <div className="ml-8 flex flex-row items-center justify-start gap-2">
          <p>
            <span className="text-tertiary text-lg font-bold">
              {KClCalculated.toFixed(1)}
            </span>{' '}
          </p>
          <Label className="text-foreground text-sm font-light">
            mL de KCl 19,1%, IV de 12/12 horas
          </Label>
        </div>
      </div>
    </div>
  );
}
